/** Scoreboard: the weekly season. Points buy influence — a pick, a veto, bragging rights — never outcomes. */
import { Chip } from '@/components/chip';
import { CoopCard, type Coop } from '@/components/coop-card';
import { FaceDownCard } from '@/components/face-down-card';
import { Panel } from '@/components/panel';
import { GOLD_GRADIENT } from '@/components/stamp-band';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead, CardTitle, useCardEdge } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { useReducedMotion } from '@/lib/motion';
import { otherPartner, partnerTint, type PartnerId, type PartnerTint } from '@/lib/partner';
import { cn } from '@/lib/utils';
import { api } from '@/api';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { usePoll } from '@/usePoll';
import { useStore, whoLabel, type Look, type Me } from '@/store';
import * as React from 'react';
import { Platform, Pressable, View } from 'react-native';

interface AgendaDef {
  id: string;
  title: string;
  description: string;
}

/**
 * Where a week's points came from — the server's own `point_events.kind` vocabulary.
 *
 * Closed rather than an open string, because the breakdown is drawn in a FIXED order with a human
 * label per kind: a kind this build has never heard of has no position and no word, so it would
 * render as a blank row claiming some number of points came from nowhere.
 */
type PointKind =
  | 'mission_done'
  | 'list_done'
  | 'gratitude'
  | 'gratitude_streak'
  | 'sync_predict_hit'
  | 'coop_success'
  | 'wager'
  | 'agenda_jackpot';

interface Deadlines {
  reveal: string;
  agendaClose: string;
  wagerClose: string;
}

interface RevealData {
  totals: { A: number; B: number };
  margin: number;
  band: 'tie' | 'clean' | 'win_no_pick' | 'greedy';
  winner: 'A' | 'B' | null;
  pickAvailable: boolean;
  agendas: { A: AgendaDef | null; B: AgendaDef | null };
  jackpot: boolean;
  wagers: { A: { goal: string; status: string; delta: number } | null; B: { goal: string; status: string; delta: number } | null };
  /**
   * The week's co-op challenge as it stood when the card was turned over.
   *
   * Frozen onto the season row by `executeReveal` and, until now, never drawn: the one thing on
   * the scoreboard that neither of them wins alone was missing from the only moment that reads the
   * week back to them. Optional for the same reason `round` is — a sidecar one deploy behind sends
   * a reveal blob without it, and an absent section is honest where a zeroed one is a claim.
   */
  coop?: { title: string; status: string; progress: number; target: number; misses: number; budget: number } | null;
}

interface ScoreState {
  weekStart: string;
  status: 'open' | 'revealed';
  revealAllowed: boolean;
  totals: { me: number; partner: number };
  catchUp: { active: boolean; multiplier: number };
  agenda: { options: AgendaDef[]; mine: string | null; partnerPicked: boolean; jackpot: number };
  wagers: {
    mine: { goal: string; status: string } | null;
    partner: { goal: string; status: string } | null;
    stake: number;
    window: boolean;
  };
  coop: Coop | null;
  coopTemplates: { title: string; detail: string; target: number; budget: number }[];
  lifetime: { me: number; partner: number };
  reveal: RevealData | null;
  winnerPick: { type: string; detail: string | null; by: 'A' | 'B' } | null;

  /**
   * The three below are OPTIONAL, and that is a claim about this branch rather than about the API.
   *
   * A season index, its two deadlines and a per-kind breakdown are all fresh columns on
   * `GET /api/score`, so a client running against a sidecar one deploy behind gets a payload
   * without them. Optional means the round bar and the breakdown columns are simply absent there —
   * a missing section, which is honest — rather than a screen that throws reading `.reveal` off
   * `undefined` and renders nothing at all.
   */
  /** 1-based index of this week within the season. */
  round?: number;
  /** When the week's three doors open and shut, as instants (ISO UTC) rather than local wording. */
  deadlines?: Deadlines;
  /** This week's points, per side, split by what earned them. Zero-valued kinds may be absent. */
  breakdown?: { me: Partial<Record<PointKind, number>>; partner: Partial<Record<PointKind, number>> };

  /**
   * THIS BOARD IS A PAST WEEK, held open because nobody lifted its card on the Sunday.
   *
   * The server reaches backwards for the oldest week still waiting for its reveal, so a week
   * nobody sat down for is no longer gone by Monday morning. The consequence for this screen is
   * that `weekStart` is not necessarily the week the calendar is in — so when this is true the
   * panel SAYS which week it is showing and why, and it draws only the reveal: sealing an agenda
   * or staking a wager belongs to the live week, and a form that quietly posts into a different
   * seven days from the ones printed above it is the worst version of this bug, not the fix.
   */
  awaitingReveal?: boolean;
}

const BAND_COPY = {
  tie: { title: 'Dead heat', text: 'Nobody wins, nobody loses. Suspiciously diplomatic.' },
  clean: { title: 'Clean win', text: 'Won by just enough — the winner takes the pick.' },
  win_no_pick: { title: 'Win — no pick', text: 'A solid win, but too solid for a prize.' },
  greedy: { title: 'Greedy!', text: 'Won by 20+. That is a blowout — no winner this week. (Fox in the Forest rules.)' },
};

const PICK_TYPES = [
  { id: 'date_choice', label: 'I choose the next date' },
  { id: 'sealed_scratch', label: 'I scratch a sealed card' },
  { id: 'request', label: 'One (veto-able) request' },
];

const pickLabel = (type: string) => PICK_TYPES.find((p) => p.id === type)?.label ?? type;

/**
 * The breakdown's reading order, and it is deliberately not sorted by size.
 *
 * A column whose rows reorder between the two sides — or between two Sundays — cannot be compared
 * down the page, which is the whole job of putting them side by side. So the order is the shape of
 * the week (the things you do, then the things you bet), fixed, and only the numbers move.
 */
const KIND_ORDER: PointKind[] = [
  'mission_done',
  'list_done',
  'gratitude',
  'gratitude_streak',
  'sync_predict_hit',
  'coop_success',
  'wager',
  'agenda_jackpot',
];

const KIND_LABEL: Record<PointKind, string> = {
  mission_done: 'Missions',
  list_done: 'List',
  gratitude: 'Thanks',
  gratitude_streak: 'Streak',
  sync_predict_hit: 'Sync hits',
  coop_success: 'Co-op',
  wager: 'Wager',
  agenda_jackpot: 'Agenda',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * "31 Aug" out of the week's 'YYYY-MM-DD' start.
 *
 * Split rather than `new Date(iso)`: a bare date string parses as UTC midnight, so anywhere west of
 * Greenwich a locale formatter renders the day BEFORE — a week labelled one day early, on every
 * screen, silently. The string already says what the server meant, so it is read rather than parsed.
 */
function formatWeek(iso: string): string {
  const parts = iso.split('-');
  const month = MONTHS[Number(parts[1]) - 1];
  const day = Number(parts[2]);
  return month && Number.isFinite(day) ? `${day} ${month}` : iso;
}

/**
 * "Sun 7pm", "Tue 11pm", "Tue 11:30pm" — an instant read on the device's own clock.
 *
 * Deadlines cross the wire as UTC and are read here in local time, because "reveal Sun 7pm" is a
 * promise about the evening the two of them are sitting in. Minutes appear only when there are any:
 * "Sun 7:00pm" reads as a precision the deadline does not have.
 */
function formatDeadline(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  const day = WEEKDAYS[at.getDay()] ?? '';
  const h24 = at.getHours();
  const hour = h24 % 12 === 0 ? 12 : h24 % 12;
  const min = at.getMinutes();
  return `${day} ${hour}${min ? `:${String(min).padStart(2, '0')}` : ''}${h24 < 12 ? 'am' : 'pm'}`;
}

/**
 * "2 days 4 hours", "4 hours 12 minutes", "6 minutes" — how long is left, coarsely.
 *
 * Two units and never three: this is a note telling you roughly when to come back, and a ticking
 * "2d 4h 17m 3s" is a stopwatch, which invites watching. Null under a minute, so the caller can
 * say "any moment now" rather than the arithmetic "in 0m".
 *
 * The units are WORDS. "REVEAL IN 3D 2H" was a countdown written the way a dashboard writes one,
 * and this row is a sentence somebody reads once a week.
 */
function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

function timeLeft(ms: number): string | null {
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return null;
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days) return hours ? `${plural(days, 'day')} ${plural(hours, 'hour')}` : plural(days, 'day');
  if (hours) return mins % 60 ? `${plural(hours, 'hour')} ${plural(mins % 60, 'minute')}` : plural(hours, 'hour');
  return plural(mins, 'minute');
}

/**
 * Two characters for an avatar, and it is HOME's rule rather than a second one.
 *
 * This used to take the first letter of the first word and the first of the last, which gives two
 * characters for "Ada Lovelace" and exactly one for everybody who signs up with a first name —
 * so the board drew "G" and "G" for Greg and Gabi, two identical tiles on the one card whose whole
 * job is telling the two of them apart. Home has always cut it as first letter plus second letter,
 * which is "Gr" and "Ga", and one app does not get two ways of writing the same couple's initials.
 * Falls back to the partner id for a name with nothing usable in it.
 */
function initials(name: string | undefined, fallback: PartnerId): string {
  const trimmed = (name ?? '').trim();
  return trimmed ? trimmed[0]!.toUpperCase() + trimmed.slice(1, 2).toLowerCase() : fallback;
}

/**
 * A clock that ticks slowly.
 *
 * The countdown it drives is rendered in days and hours, so thirty seconds is already far finer
 * than anything it can change; a per-second interval would repaint the whole panel — the co-op
 * dial included — sixty times a minute to redraw the same two numbers.
 */
function useNow(periodMs: number): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), periodMs);
    return () => clearInterval(id);
  }, [periodMs]);
  return now;
}

/**
 * THE WEEK'S RESULT, ARRIVING RATHER THAN BEING PRESENT — 0 → 1 over `ms`, once, on mount.
 *
 * The card flies off and the totals were simply THERE, finished, which is a receipt: the whole
 * point of making somebody physically lift the thing is that the number underneath was worth the
 * gesture. Counting them up is the cheapest way to make the result an event, and the verdict lands
 * at the end of it rather than at the start, so the band name is a conclusion and not a caption.
 *
 * An interval and not `requestAnimationFrame`: this is one number redrawn about twenty-five times
 * over a second and a bit, rAF is not the same API on all three platforms this ships to, and a
 * frame-accurate count-up would be a precision nobody can see.
 *
 * Reduced motion gets 1 immediately — the finished totals and the verdict, at once. Nothing here
 * carries information the static rendering does not, which is exactly what makes it droppable.
 */
const COUNT_UP_MS = 1200;
const COUNT_UP_TICK = 45;

function useCountUp(runKey: string | null, ms: number): number {
  const reduced = useReducedMotion();
  // Lazy, so a panel entered on an already-revealed week starts at zero rather than painting the
  // final figures for one frame and then dropping them to nothing.
  const [progress, setProgress] = React.useState(() => (runKey && !reduced ? 0 : 1));

  React.useEffect(() => {
    if (!runKey || reduced) {
      setProgress(1);
      return;
    }
    setProgress(0);
    const started = Date.now();
    const id = setInterval(() => {
      const t = Math.min(1, (Date.now() - started) / ms);
      setProgress(t);
      if (t >= 1) clearInterval(id);
    }, COUNT_UP_TICK);
    return () => clearInterval(id);
  }, [runKey, ms, reduced]);

  return progress;
}

/**
 * Ease-out, so the figures slow into their final value rather than stopping dead on it.
 *
 * Cubic, which is the one every other easing in this app would be if any of them were written
 * down: a linear count reads as a machine ticking over, and a number that decelerates reads as one
 * settling.
 */
const easeOut = (t: number) => 1 - (1 - t) ** 3;

export function ScoreScreen() {
  // `look` rides the same whole-store read the screen already takes: the sealed stamp is a gold
  // STICKER on Bouquet and a gold-ruled box on Tuxedo — a different object, not a recoloured one.
  const { me, invitesFor, refreshInvites, refreshBadges, fail, look } = useStore();
  const [state, setState] = React.useState<ScoreState | null>(null);
  const [wagerGoal, setWagerGoal] = React.useState('');
  // The agenda is secret and worth a jackpot, so choosing one and committing to it are two acts.
  const [agendaPick, setAgendaPick] = React.useState<string | null>(null);
  const now = useNow(30_000);

  /**
   * THE WEEK YOU JUST TURNED OVER, held on screen until you say you are done with it.
   *
   * A held past week stops being the target the instant it is revealed — that is the whole point of
   * `targetSeason`, and it is right — but it meant the reveal you had just dragged open vanished on
   * the refetch that followed it, and the board came back showing a different seven days. The count
   * up, the verdict, both wagers and the co-op result all existed and none of them were ever seen,
   * which is the precise failure the reveal was rebuilt to fix.
   *
   * So the POST's own answer is kept. While it is here the panel is that week and nothing else, and
   * the only way on is a button that says so — a refetch you asked for rather than one that happened
   * to you. The ordinary Sunday reveal does not come through here at all: there the board's own week
   * IS the revealed one, so the refetch shows exactly what was just turned over.
   */
  const [justRevealed, setJustRevealed] = React.useState<(RevealData & { weekStart: string }) | null>(null);

  const load = React.useCallback(async () => {
    setState(await api.get<ScoreState>('/api/score'));
  }, []);

  const { loaded, error, reload } = usePoll(load, 25_000);

  /**
   * ONE CLOCK FOR THE WHOLE REVEAL, held here because two pieces of the screen have to share it.
   *
   * The head-to-head's numerals count up and the verdict lands when they stop, and those are two
   * components — so the progress cannot live in either of them without the other having to guess.
   * Keyed on the revealed week: it restarts when a week is turned over (and when the panel is
   * re-entered on an already-revealed one, which is the same moment played back), and sits at 1 for
   * every other state, so nothing else on the board ever animates.
   *
   * Above the early returns because it is a hook, and the loading and error branches below are not
   * allowed to change how many of those this component calls.
   */
  const countUp = useCountUp(
    justRevealed ? justRevealed.weekStart : state?.status === 'revealed' && state.reveal ? state.weekStart : null,
    COUNT_UP_MS,
  );

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await Promise.all([load(), refreshBadges(), refreshInvites()]);
    } catch (e) {
      fail(e);
    }
  };

  /** What CoopCard calls after it changes something. Same refresh, no return value. */
  const refresh = async () => {
    await Promise.all([load(), refreshBadges(), refreshInvites()]);
  };

  /**
   * The reveal is one POST, and `FaceDownCard` owns everything around it — the drag, the tap, the
   * busy latch and the flight. This is only the request and the refetch after it, and it THROWS so
   * the card comes back rather than leaving a hole where the result would have been.
   */
  const doReveal = async () => {
    try {
      const result = await api.post<RevealData & { weekStart: string }>('/api/score/reveal');
      // Only a HELD week needs holding: on the ordinary Sunday the board's own week is the one that
      // was just revealed, so the refetch below shows it and a stashed copy would be the same card
      // drawn from a staler source.
      if (state?.awaitingReveal && result?.weekStart) setJustRevealed(result);
    } catch (e) {
      fail(e);
      throw e;
    }
  };

  /** What the card calls once its flight has finished. A held week waits; every other week refetches. */
  const afterReveal = () => {
    if (!state?.awaitingReveal) void refresh();
  };

  /** Done looking at the week that was turned over — go and fetch the one the clock is in. */
  const startThisWeek = () => {
    setJustRevealed(null);
    void refresh();
  };

  if (!loaded) return (
    <Panel id="score">
      <PanelSkeleton rows={3} />
    </Panel>
  );
  if (error) return (
    <Panel id="score">
      <LoadFailed onRetry={reload} reason={error} />
    </Panel>
  );

  if (!state) return <Panel id="score">{null}</Panel>;

  const s = state;
  /** The tint identity to draw the viewer in. 'A' while `me` is unknown, which is the first paint. */
  const meId: PartnerId = me?.partner ?? 'A';
  const myName = me?.name ?? 'You';
  const theirName = me?.partnerName ?? 'Partner';
  /** "Gabi’s", STANDALONE — "Gabi’s · sealed". `possessiveOf` builds the attributive form. */
  const theirPossessive = me?.partnerName ? possessiveOf(me.partnerName) : 'Theirs';
  const coopInvites = invitesFor('score');
  const myAgenda = s.agenda.mine ? s.agenda.options.find((a) => a.id === s.agenda.mine) : undefined;
  /**
   * THE BOARD IS SHOWING A WEEK THAT IS OVER, and everything below reads off this one flag.
   *
   * The three commitment cards — agenda, wager, co-op — all post into the week the CLOCK is in,
   * because that is what a pre-commitment is. So while a past week is standing here waiting to be
   * turned over, they are not drawn at all: a "Seal it" under a head-to-head captioned "Week of 20
   * Jul" would seal into the 27th, silently, which is a worse bug than the one this fixes. One
   * thing to do, and it is the thing the week is waiting for.
   */
  const held = !!s.awaitingReveal;
  /**
   * How long until the reveal door opens, for the locked card's face.
   *
   * `undefined` where the payload carries no deadlines at all — a sidecar one deploy behind — and
   * the card drops the line rather than printing a countdown to a moment it cannot name. `null` is
   * the real "under a minute" answer, which the card writes as "any moment now".
   */
  const revealAt = s.deadlines ? Date.parse(s.deadlines.reveal) : NaN;
  const revealLeft = Number.isFinite(revealAt) ? timeLeft(revealAt - now) : undefined;

  /**
   * THE HELD WEEK, JUST TURNED OVER — the only thing on the panel until it is dismissed.
   *
   * Drawn from the POST's own answer rather than from `s`, because `s` is about to be (or already
   * is) a different week: the moment a stale season is revealed it stops being the target. The
   * totals come off the reveal too, not off the board, since `executeReveal` resolves both wagers
   * and pays the jackpot — so the board's numbers are the ones from BEFORE the card was turned over,
   * which is the one set of figures a reveal must not show.
   */
  if (justRevealed) {
    const totals = { me: justRevealed.totals[meId], partner: justRevealed.totals[otherPartner(meId)] };
    return (
      <Panel id="score">
        <HeadToHead
          meId={meId}
          myName={myName}
          theirName={theirName}
          totals={totals}
          weekStart={justRevealed.weekStart}
          breakdown={undefined}
          held={false}
          countUp={countUp}
        />
        <RevealCard
          reveal={justRevealed}
          jackpotPoints={s.agenda.jackpot}
          winnerPick={null}
          me={me}
          look={look}
          landed={countUp >= 1}
          onPick={(type, detail) => run(() => api.post('/api/score/pick', { type, detail }))}
        />
        <Button onPress={startThisWeek}>
          <Text>Start this week</Text>
        </Button>
      </Panel>
    );
  }

  return (
    <Panel id="score">
      <RoundBar round={s.round} deadlines={s.deadlines} now={now} weekStart={held ? s.weekStart : undefined} />

      <HeadToHead
        meId={meId}
        myName={myName}
        theirName={theirName}
        totals={s.totals}
        weekStart={s.weekStart}
        breakdown={s.breakdown}
        held={held}
        countUp={countUp}
      />

      {/* A badge is a LABEL — tracked capitals, two or three words — and this one was carrying a
          whole sentence, wrapping onto a second line of shouting. The multiplier is the label; what
          it means is the note under it. Not while a past week is held: the catch-up multiplier is a
          fact about the week you are PLAYING, and this is not it. */}
      {s.catchUp.active && !held ? (
        <View className="gap-1">
          <Badge variant="warning" className="self-start">
            <Text>Catch-up ×{s.catchUp.multiplier}</Text>
          </Badge>
          <Text variant="note">Your missions pay ×{s.catchUp.multiplier} while you trail.</Text>
        </View>
      ) : null}

      {s.status === 'revealed' && s.reveal ? (
        <RevealCard
          reveal={s.reveal}
          jackpotPoints={s.agenda.jackpot}
          winnerPick={s.winnerPick}
          me={me}
          look={look}
          landed={countUp >= 1}
          onPick={(type, detail) => run(() => api.post('/api/score/pick', { type, detail }))}
        />
      ) : s.revealAllowed ? (
        <FaceDownCard
          lift={{
            name: 'Reveal the week',
            hint: 'Move the card up, or activate it, to run the Sunday reveal',
            label: 'Move up to reveal',
            body: held
              ? `Week of ${formatWeek(s.weekStart)}, still face down. Lift it and this week starts.`
              : 'The week is face down until one of you lifts it.',
            onReveal: doReveal,
            onRevealed: afterReveal,
          }}
        />
      ) : (
        /**
         * MID-WEEK, THE SLOT IS THE CARD — face down, with the clock on it.
         *
         * This was one grey line of body text ("Reveal opens Sunday evening. Play the week.") in the
         * place a card is going to be, so for six days out of seven the biggest thing on the panel
         * was an orphaned sentence and the object it describes did not exist until the moment it
         * became liftable. The card is here all week now, hatched and locked, counting down — the
         * same object in its earlier state, which is what makes Sunday a door opening rather than a
         * new thing appearing.
         */
        <FaceDownCard
          locked={{
            label: 'Reveal opens Sunday evening',
            countdown: revealLeft,
            body: 'Play the week. Nobody can turn this over early — not even you.',
          }}
        />
      )}

      {held ? (
        /* The one sentence that stops this being a trap. Everything below it belongs to the week
           the clock is in, and none of it is drawn until the card above has been turned over. */
        <Text variant="small" className="text-muted-foreground text-center">
          Sealing an agenda, staking a wager and proposing a challenge come back once this one has been turned over.
        </Text>
      ) : (
        <>
          <Card>
            <CardContent>
              <CardHead>Secret agenda</CardHead>
              {!s.agenda.mine ? (
                <>
                  <CardDescription>
                    Pick one, then seal it — sealed is final for the week. If you both sealed the same one: +
                    {s.agenda.jackpot} each at the reveal.
                  </CardDescription>
                  {/**
                   * SIX OPTIONS AS ONE CHOICE, and the commitment under them rather than beside them.
                   *
                   * They used to be six equal-weight cards each holding one six-word line, which on a
                   * desktop was six 1320px slabs, and "Seal it" — the weekly commitment this whole
                   * panel is built around — was a small left-aligned button underneath them at 2.43:1.
                   * The list stops at a readable measure now, the picked one takes the tonal state
                   * (`--accent` with a strong hairline, the same reading `Chip tone="tonal"` uses when
                   * a row of choices stands above its own call to action), and the button is the
                   * full-width primary that closes the card.
                   *
                   * Bespoke Pressables rather than Chips on purpose: each option is a title AND a
                   * description, which is two lines and a shape a chip does not have.
                   */}
                  <View className="w-full max-w-[560px] gap-2">
                    {s.agenda.options.map((a) => {
                      const picked = agendaPick === a.id;
                      return (
                        <Pressable
                          key={a.id}
                          accessibilityRole="button"
                          accessibilityState={{ selected: picked }}
                          onPress={() => setAgendaPick(a.id)}
                          className={cn(
                            'gap-0.5 rounded-md border p-3',
                            picked
                              ? 'bg-accent border-line-strong'
                              : 'bg-card border-border hover:border-line-strong hover:bg-accent/40 active:bg-accent',
                          )}>
                          <Text className={cn('font-medium', picked && 'text-accent-foreground font-semibold')}>
                            {a.title}
                          </Text>
                          <Text variant="small" className="text-muted-foreground">
                            {a.description}
                          </Text>
                        </Pressable>
                      );
                    })}
                    <Button
                      className="w-full"
                      disabled={!agendaPick}
                      reason="Pick one first"
                      onPress={() => run(() => api.post('/api/score/agenda', { agendaId: agendaPick }))}>
                      <Text>Seal it</Text>
                    </Button>
                    {agendaPick ? (
                      <Button variant="ghost" flush={false} className="self-start" onPress={() => setAgendaPick(null)}>
                        <Text>Clear</Text>
                      </Button>
                    ) : null}
                  </View>
                </>
              ) : (
                /**
                 * Two held secrets, side by side — mine face up because it is mine, theirs face down
                 * because the whole mechanic is that I do not get to know it. They wrap rather than
                 * splitting on a breakpoint: `Side` and `Stat` set that idiom, and a basis is what puts
                 * them beside each other exactly when there is room for both to be readable.
                 */
                <View className="flex-row flex-wrap gap-3">
                  {/* `card-2` and an 8px shoulder: the sheets' `.sealed` is nested paper on the card,
                      not a hole cut through to the ground. */}
                  <View className="bg-card-2 border-border min-w-0 grow basis-[220px] gap-2 rounded-md border p-4">
                    {/**
                     * THE TITLE SHRINKS AND THE STAMP WRAPS UNDER IT, in that order.
                     *
                     * The stamp is a fixed-width rotated object, so at 320 it took its 118px out of a
                     * 210px column and left the title 90 to break "Slow evening" into "Slow / eveni /
                     * ng" — a word broken mid-syllable on the one card that is announcing a secret.
                     * `flex-1 min-w-0` lets the title actually give ground (a `shrink` alone will not
                     * go below its longest word), and `flex-wrap` on the row drops the stamp onto its
                     * own line when even that is not enough. No breakpoint: the row wraps when it does
                     * not fit, which is the only width test this app is allowed to make.
                     */}
                    <View className="flex-row flex-wrap items-start justify-between gap-2">
                      <Text variant="heading" className="min-w-0 flex-1">
                        {myAgenda?.title ?? 'Sealed'}
                      </Text>
                      <SealMark label="Sealed until Sunday" look={look} />
                    </View>
                    {myAgenda?.description ? (
                      <Text variant="small" className="text-muted-foreground">
                        {myAgenda.description}
                      </Text>
                    ) : null}
                  </View>

                  {/**
                   * THE PARTNER'S SLOT NAMES THE RULE, NEVER THE PERSON.
                   *
                   * "Greg hasn't picked yet" is a receipt about somebody's inaction on a 210px hatched
                   * card, which is the nudge rule broken as plainly as it can be — nothing here may
                   * frame the other one as having failed to act. Both states say the same thing about
                   * the WEEK: agendas are sealed until Sunday, and this one is face down either way.
                   * Whether they have sealed is still readable, because it is the tension the jackpot
                   * runs on, but it is a fact about a card and not a verdict about a person.
                   */}
                  <FaceDownCard className="min-w-0 grow basis-[220px]">
                    <Text variant="small" className="text-muted-foreground text-center">
                      {s.agenda.partnerPicked ? `${theirPossessive} · sealed` : 'Both agendas seal on Sunday'}
                    </Text>
                  </FaceDownCard>
                </View>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <CardHead>Week wager</CardHead>
              {!s.wagers.mine ? (
                s.wagers.window ? (
                  <>
                    <CardDescription>
                      Double-or-nothing on a personal goal: hit pays +{s.wagers.stake * 2}, miss costs −{s.wagers.stake}.
                      Closes Tuesday night.
                    </CardDescription>
                    <View className="w-full max-w-[560px] gap-2">
                      <Input
                        value={wagerGoal}
                        onChangeText={setWagerGoal}
                        placeholder="e.g. run three times"
                        maxLength={200}
                      />
                      {/* The same shape the agenda now has, and for the same reason: the field is the
                          choice, and the commitment is the full-width thing that closes it. */}
                      <Button
                        className="w-full"
                        disabled={!wagerGoal.trim()}
                        reason="Name the goal first"
                        onPress={() => {
                          const goal = wagerGoal.trim();
                          setWagerGoal('');
                          void run(() => api.post('/api/score/wager', { goal }));
                        }}>
                        <Text>Stake it</Text>
                      </Button>
                    </View>
                  </>
                ) : (
                  <CardDescription>
                    Wager window is closed for this week (Mon–Tue only) — pre-commitment is the mechanic.
                  </CardDescription>
                )
              ) : (
                <>
                  {/* A staked wager is a sealed thing too — same gold mark, same words about the same
                      Sunday. It is the one visual that says "this is now out of your hands". */}
                  <View className="flex-row flex-wrap items-start justify-between gap-2">
                    <Text className="min-w-0 flex-1">
                      Yours: “{s.wagers.mine.goal}” — <Text className="font-semibold">{s.wagers.mine.status}</Text>
                    </Text>
                    {s.wagers.mine.status === 'active' ? <SealMark label="Staked until Sunday" look={look} /> : null}
                  </View>
                  {s.wagers.mine.status === 'active' ? (
                    <View className="flex-row flex-wrap gap-2">
                      <Button onPress={() => run(() => api.post('/api/score/wager/resolve', { status: 'hit' }))}>
                        <Text>I hit it</Text>
                      </Button>
                      {/* The tertiary of the pair: a word you can press, which is what a ghost is. */}
                      <Button variant="ghost" flush={false} onPress={() => run(() => api.post('/api/score/wager/resolve', { status: 'missed' }))}>
                        <Text>I missed</Text>
                      </Button>
                    </View>
                  ) : null}
                </>
              )}
              {s.wagers.partner ? (
                <Text variant="small" className="text-muted-foreground">
                  {me?.partnerName ?? 'They'}: “{s.wagers.partner.goal}” — {s.wagers.partner.status}
                </Text>
              ) : null}
            </CardContent>
          </Card>

          {s.coop ? (
            <CoopCard coop={s.coop} invites={coopInvites} onChange={refresh} />
          ) : (
            <CoopProposal
              templates={s.coopTemplates}
              onPropose={(body) => run(() => api.post('/api/score/coop', body))}
            />
          )}
        </>
      )}

      {/* `All-time · You 4 · Gabi 23` — one separator doing one job. It read "All-time: you 4 ·
          Gabi 23", which mixes a colon with the dots and lower-cases one of the two players beside
          a capitalised name, so the line had three punctuation ideas and two ways of writing a
          person. Middots throughout, and both sides are Names. */}
      <Text variant="small" className="text-muted-foreground text-center">
        All-time · You {s.lifetime.me} · {me?.partnerName ?? 'Partner'} {s.lifetime.partner} (bragging rights only —
        points reset weekly)
      </Text>
    </Panel>
  );
}

/**
 * The season, and its two doors, on one line.
 *
 * Three sentences of "reveal opens Sunday evening", "the wager window is Mon–Tue" and a week start
 * with no number on it were the same facts spread across three cards, none of which said how long
 * was left. The round has an index because a season without one has no shape; the deadlines are one
 * row because they are read together — what can I still change, and when do I find out.
 */
function RoundBar({
  round,
  deadlines,
  now,
  weekStart,
}: {
  round?: number;
  deadlines?: Deadlines;
  now: number;
  /**
   * The week this board is standing on, and PRESENT ONLY when that is not the week the calendar is
   * in — a past round held open because nobody turned its card over.
   *
   * It goes at the top of the bar rather than into the doors line beside it, because it is not a
   * deadline: it is which seven days everything under it is about. Absent on an ordinary week, so
   * the bar keeps its usual shape and only says this when there is something to say.
   */
  weekStart?: string;
}) {
  if (round == null && !deadlines && !weekStart) return null;

  /**
   * The two closes are one segment when they are one instant, and two when they are not.
   *
   * The line is written as "agenda & wager close Tue 11pm" because that is what the season does
   * today. Merging them unconditionally would be this bar asserting a fact about the config it
   * cannot see — so the merge is conditional on the instants actually agreeing.
   */
  const merged = !!deadlines && deadlines.agendaClose === deadlines.wagerClose;
  const doors = deadlines
    ? merged
      ? [
          { iso: deadlines.reveal, open: 'reveal', past: 'reveal is open', name: 'Reveal' },
          { iso: deadlines.agendaClose, open: 'agenda & wager close', past: 'agenda & wager closed', name: 'Agenda & wager close' },
        ]
      : [
          { iso: deadlines.reveal, open: 'reveal', past: 'reveal is open', name: 'Reveal' },
          { iso: deadlines.agendaClose, open: 'agenda closes', past: 'agenda closed', name: 'Agenda closes' },
          { iso: deadlines.wagerClose, open: 'wager closes', past: 'wager closed', name: 'Wager closes' },
        ]
    : [];

  const dated = doors.map((d) => ({ ...d, at: Date.parse(d.iso) })).filter((d) => Number.isFinite(d.at));
  const line = dated.map((d) => (now >= d.at ? d.past : `${d.open} ${formatDeadline(d.iso)}`)).join(' · ');
  // The nearest door still ahead. Sorted rather than assumed: which of the three comes first
  // changes as the week runs, and it is the only one worth counting down to.
  const next = dated.filter((d) => d.at > now).sort((a, b) => a.at - b.at)[0];
  const left = next ? timeLeft(next.at - now) : null;
  const edge = useCardEdge();

  return (
    // `useCardEdge` rather than a look branch here: it is the one place that decides whether a
    // card carries a visible hairline, and the round bar is drawn as a card in both sheets.
    <View className={cn('bg-card gap-0.5 rounded-lg px-4 py-3', edge)}>
      {weekStart ? (
        <Text variant="small" className="text-foreground font-semibold">
          Showing last week · {formatWeek(weekStart)} — its reveal is still waiting.
        </Text>
      ) : null}
      <Text variant="small" className="text-muted-foreground">
        {round == null ? null : (
          // The round number is the one numeral in this row, and both sheets set it in the display
          // face — `.roundbar-line strong` — so it is a figure rather than a bolder word.
          <Text variant="small" className="text-foreground font-display tabular-nums font-semibold">
            Round {round}
          </Text>
        )}
        {round != null && line ? ' · ' : ''}
        {line}
      </Text>
      {next ? (
        <Text variant="note">
          {next.name} {left ? `in ${left}` : 'any moment now'}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * The week as a contest: two sides, one bar between them, and where each side's points came from.
 *
 * Two Stat tiles said 14 and 9 and nothing else — the same two numbers a scoreboard has always
 * shown, with no sense of the gap and no reason for either. A proportional bar makes the margin
 * readable without arithmetic, and the two breakdown columns are what turn "you are behind" into
 * "you are behind on Sync hits", which is the only version of it anybody can act on.
 */
function HeadToHead({
  meId,
  myName,
  theirName,
  totals,
  weekStart,
  breakdown,
  held,
  countUp,
}: {
  meId: PartnerId;
  myName: string;
  theirName: string;
  totals: { me: number; partner: number };
  weekStart: string;
  breakdown?: { me: Partial<Record<PointKind, number>>; partner: Partial<Record<PointKind, number>> };
  /** The week under the bar is a PAST one, held open for its reveal. The caption says which. */
  held?: boolean;
  /** 0…1 while the reveal's totals are arriving; 1 the rest of the time. See `useCountUp`. */
  countUp: number;
}) {
  /**
   * The look, read here rather than passed down: both sheets draw this card differently enough
   * that the difference is structural — Bouquet's numerals take each partner's own type colour and
   * Tuxedo's are both gold, and its breakdown is a ruled table where Bouquet's is a plain list.
   * Subscribing re-renders the card when Settings switches theme, which is also what makes the
   * `partnerTint` calls below read the gels of the look that is actually on.
   */
  const look = useStore((s) => s.look);
  const theirId = otherPartner(meId);
  const myTint = partnerTint(meId);
  const theirTint = partnerTint(theirId);

  /**
   * A wager can take a total negative, and a bar has no way to draw a negative share — so the
   * split is over the non-negative parts, and a week where both sides are at or below zero splits
   * even. Even is the honest answer there: nobody is ahead.
   */
  const mineShare = Math.max(0, totals.me);
  const theirShare = Math.max(0, totals.partner);
  const even = mineShare + theirShare === 0;

  /**
   * The rows BOTH columns draw — every kind either side scored anything on, in the reading order.
   *
   * Computed once for the pair rather than per column, which is the whole fix: a column that
   * filters its own zeroes cannot line up with one that filtered different zeroes, and the two of
   * them side by side exist to be read across. A kind neither of them touched still stays out — an
   * unentered category is not a fact about the week — so the list only ever grows to what happened.
   */
  const earnedKinds = KIND_ORDER.filter(
    (k) => (breakdown?.me[k] ?? 0) !== 0 || (breakdown?.partner[k] ?? 0) !== 0,
  );

  return (
    <Card moment>
      <CardContent>
        {/* The head-to-head has no title element, so the avatars ARE its title row — which is what
            wears the band, in both sheets (`.h2h-top`). */}
        {/**
         * THE FIXTURE IS A CENTRED CLUSTER, not two names pinned to the far edges of the card.
         *
         * `CardHead row` pushes its pieces apart, which is right for a title and a chip and wrong
         * for a matchup: on a desktop it put Greg and Gabi nine hundred pixels either side of a
         * 13px "vs", which is three separate objects rather than one fixture. The band still runs
         * the full width — it is the card's own header — and the three pieces sit inside a
         * `MEASURE`-width row within it, so the pairing reads as a pairing at every size.
         */}
        <CardHead className="items-center">
          <View className="w-full max-w-[560px] flex-row items-center justify-between gap-3">
            <MatchupName id={meId} name={myName} tint={myTint} look={look} />
            {/**
             * `vs` is set in the DISPLAY face in both sheets, which is why it is not `variant="label"`
             * — that one is the body face, and the label step's 11px/1.5px tracking made the word read
             * as a column head sitting on the band rather than as the fixture it is. Bouquet italicises
             * and lowercases it (the sheet's `.vs`); Tuxedo sets it upright, capital and wider-tracked.
             */}
            <Text
              className={cn(
                'font-display text-band-ink shrink-0 text-[13px] font-semibold leading-[16px]',
                look === 'bouquet' ? 'italic lowercase tracking-[1.3px]' : 'uppercase tracking-[1.56px]',
              )}>
              vs
            </Text>
            <MatchupName id={theirId} name={theirName} tint={theirTint} look={look} trailing />
          </View>
        </CardHead>

        {/* The signature object: the booth's numerals, read against the CARD and never the band.
            On the Sunday they arrive rather than appear — `countUp` runs them from zero, and the
            verdict on the card below lands when they stop. Rounded off the eased progress, so the
            last few frames are the same slowing count both figures make. */}
        <View className="flex-row items-baseline justify-between">
          <ScoreNumeral value={Math.round(totals.me * easeOut(countUp))} tint={myTint} look={look} />
          <ScoreNumeral value={Math.round(totals.partner * easeOut(countUp))} tint={theirTint} look={look} />
        </View>

        <View className="gap-1.5">
          <View className="bg-track h-2.5 flex-row overflow-hidden rounded-[2px]">
            <BarSeg id={meId} grow={even ? 1 : mineShare} tint={myTint} look={look} />
            <BarSeg id={theirId} grow={even ? 1 : theirShare} tint={theirTint} look={look} second />
          </View>
          {/* The one caption the two sheets disagree about in KIND: Bouquet writes the week as a
              sentence under the bar, Tuxedo as a tracked capital rubric — `.week` in each. It is
              also the second place a held week says so: this caption is directly under the numbers
              it belongs to, which is where somebody actually looks for "which week is this". */}
          <Text
            variant="small"
            className={cn(
              'text-muted-foreground text-center',
              look === 'bouquet'
                ? 'text-[13px] font-medium tracking-[0.4px]'
                : 'text-[10.5px] font-bold uppercase tracking-[1.47px]',
            )}>
            Week of {formatWeek(weekStart)}
            {held ? ' · not yet revealed' : ''}
          </Text>
        </View>

        {breakdown ? (
          /**
           * BOTH COLUMNS DRAW THE SAME ROWS, which is the only way two lists side by side can be
           * read across. Each side used to drop its own zeroes, so if you had four Sync hits and
           * they had none, "Sync hits" appeared in your column at row three and in theirs not at
           * all — and every row below it in their column had slid up to line against a different
           * label. The union of the two sides, in `KIND_ORDER`, with a nought where a side has
           * none: a zero here is not a claim about an unentered category, it is the answer to
           * "how did they do on the thing I did well".
           */
          <View className="border-border flex-row gap-4 border-t pt-3">
            <BreakdownColumn name={myName} tint={myTint} rows={breakdown.me} kinds={earnedKinds} look={look} />
            <BreakdownColumn name={theirName} tint={theirTint} rows={breakdown.partner} kinds={earnedKinds} look={look} />
          </View>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * One half of the band: the avatar and the name, mirrored on the right.
 *
 * The ring is the whole reason this is not just a tinted square. A gel keeps its hue on the band —
 * that is the identity rule — so a clay avatar on Bouquet's red panel and a midnight one on
 * Tuxedo's midnight band both need something between them, and both sheets give it an EDGE rather
 * than a second colour. Bouquet rings both in white; Tuxedo rings only B, whose midnight measures
 * 1.11:1 on its own band by day as well as by night.
 *
 * The tile takes `board` and its initials take `boardInk` — the one reading this whole card draws a
 * person in. See `PartnerTint.board`: the avatar used to take `fill` while the numeral under it
 * took `type`, so on a dark night Gabi was navy up here and pale slate six pixels below.
 */
function MatchupName({
  id,
  name,
  tint,
  look,
  trailing,
}: {
  id: PartnerId;
  name: string;
  tint: PartnerTint;
  look: Look;
  /** The right-hand side, mirrored so the two read outward from the score row below them. */
  trailing?: boolean;
}) {
  /**
   * Bouquet rings the avatar in `--band-ink`, the colour the band's own type is drawn in and the
   * one thing on it that is white in both modes — as a CLASS, because it is the only branch here
   * that is not a runtime gel and a class is the only form that reaches native as a token.
   */
  const ringClass = look === 'bouquet' ? 'border-band-ink border-2' : undefined;
  const ring =
    look === 'bouquet'
      ? undefined
      : id === 'B'
        ? { borderWidth: 2, borderColor: tint.line }
        : // The fallback is an EDGE and not "no border": Tuxedo's A gel is clay on a midnight band
          // today, but a look whose band ever met a gel of its own colour would lose the square
          // entirely — and a 1px hairline is the cheapest guarantee that it cannot vanish.
          { borderWidth: 1, borderColor: tint.line };

  return (
    <View className={cn('min-w-0 shrink flex-row items-center gap-2', trailing && 'flex-row-reverse')}>
      {/* 40px, which is the board's — the avatar is half the weight of this band, and at 28 it read
          as a bullet in front of the name rather than as the player. */}
      <View
        className={cn('size-10 shrink-0 items-center justify-center rounded-md', ringClass)}
        style={{ backgroundColor: tint.board, ...ring }}>
        <Text variant="small" className="font-display text-[18px] font-bold leading-[22px]" style={{ color: tint.boardInk }}>
          {initials(name, id)}
        </Text>
      </View>
      <Text numberOfLines={1} className="text-band-ink text-body min-w-0 shrink font-semibold">
        {name}
      </Text>
    </View>
  );
}

/**
 * A week's total, at the size the sheets set it.
 *
 * Bouquet reads the number as its owner — each numeral in that partner's own board colour, which
 * is what ties it to its half of the bar and, now, to their avatar and their swatch: one person is
 * one colour on this card. Tuxedo reads it as the prize instead: both gold, upright, at
 * Fraunces' display optical size, where its stroke contrast reaches the Didone figure the sheet
 * picked it for. `variant="display"` is kept for the face and the tabular figures; the two size
 * steps override it. `fit-opsz` is web-only: a static native instance has no `opsz` axis, so on a
 * phone the number draws at Fraunces' default optical size rather than the Didone cut.
 */
function ScoreNumeral({ value, tint, look }: { value: number; tint: PartnerTint; look: Look }) {
  return (
    <Text
      variant="display"
      className={cn('font-bold', look === 'tuxedo' ? 'fit-opsz text-gold text-[52px] leading-[56px]' : 'text-[44px] leading-[48px]')}
      style={look === 'tuxedo' ? undefined : { color: tint.board }}>
      {value}
    </Text>
  );
}

/**
 * One partner's share of the bar.
 *
 * `board`, the same colour the numeral above it and the swatch below it take. It used to be `fill`,
 * which is why UI-C3 showed Gabi's bar staying navy on a maroon card while her numeral had already
 * lifted to slate — one person, two colours, six pixels apart.
 *
 * Two decorations, one per sheet, and both are separations rather than colours: Bouquet cuts a
 * 2px card-coloured notch where the two meet, and Tuxedo outlines B with the gel's own hairline.
 * Neither is drawn on an empty share, where a border with no width behind it renders as a stray
 * sliver. The outline is belt-and-braces now rather than the only thing holding the share together
 * — `board` already clears the night track — and it stays because a hairline costs nothing and the
 * day it is the last line of defence is the day somebody retunes a token.
 */
function BarSeg({
  id,
  grow,
  tint,
  look,
  second,
}: {
  id: PartnerId;
  grow: number;
  tint: PartnerTint;
  look: Look;
  /** The right-hand share, which is the one that carries Bouquet's notch. */
  second?: boolean;
}) {
  const outlined = grow > 0 && look === 'tuxedo' && id === 'B';
  return (
    <View
      // `border-card` + a width on one side only, which is the dock rule's own idiom here: a
      // side-scoped colour utility would be the one class in this file nothing else generates.
      className={cn(grow > 0 && second && look === 'bouquet' && 'border-card border-l-2')}
      style={{
        flexGrow: grow,
        flexBasis: 0,
        backgroundColor: tint.board,
        ...(outlined ? { borderWidth: 1, borderColor: tint.line } : null),
      }}
    />
  );
}

/**
 * One side's points, by what earned them — on the row list its NEIGHBOUR is also drawing.
 *
 * `kinds` comes in from the pair rather than being filtered here, which is the difference between
 * two lists and one table: see `earnedKinds` in HeadToHead. A side with nothing on a row gets a
 * plain `0`, because the row exists on the other side and the question it is answering is a
 * comparison.
 */
function BreakdownColumn({
  name,
  tint,
  rows,
  kinds,
  look,
}: {
  name: string;
  tint: PartnerTint;
  rows: Partial<Record<PointKind, number>>;
  /** The rows BOTH columns draw, in reading order. Empty means neither side scored anything. */
  kinds: PointKind[];
  look: Look;
}) {
  /**
   * Tuxedo rules the breakdown and Bouquet does not, which is the sheets' own split: one is a
   * stat table read across, the other a short list read down. The rule is drawn on the TOP of
   * every row, head included, so the column head sits under a hairline too — the sheet's
   * `.bd-row { border-top }` with no first-row exception.
   */
  const ruled = look === 'tuxedo';

  return (
    <View className={cn('min-w-0 flex-1', ruled ? 'gap-0' : 'gap-1')}>
      <View className={cn('flex-row items-center gap-1.5', ruled && 'pb-1.5 pt-1')}>
        {/* `board`, which is what the numeral and the bar segment take too — the swatch is the
            legend for those, so a legend in a different colour from the thing it labels is the
            whole of UI-C3. The hairline stays on top of it: it costs nothing, and it is what keeps
            an 8px square from dissolving if a token is ever retuned toward the card it sits on. */}
        <View className="size-2 shrink-0 rounded-[2px]" style={{ backgroundColor: tint.board, borderWidth: 1, borderColor: tint.line }} />
        <Text variant="caption" numberOfLines={1} className="text-muted-foreground min-w-0 shrink">
          {name}
        </Text>
      </View>
      {kinds.length ? (
        kinds.map((kind) => {
          const points = rows[kind] ?? 0;
          return (
            <View
              key={kind}
              className={cn('flex-row items-center justify-between gap-2', ruled && 'border-border border-t py-2')}>
              <Text variant="small" numberOfLines={1} className="text-muted-foreground min-w-0 shrink">
                {KIND_LABEL[kind]}
              </Text>
              <Text
                variant="small"
                className={cn(
                  'font-display tabular-nums text-body font-bold',
                  // The one place the display cut needs help: at opsz 144 Fraunces sets a small,
                  // high plus tight against the figure, and "+4" arrives reading as a struck-through
                  // 4. The figures are the signature and stay at the display cut; the sign is given
                  // room. Nothing else here tracks a numeral. `fit-opsz` is web-only — no axis on a
                  // static native instance — so a phone gets the tracking and the default cut.
                  ruled && 'fit-opsz tracking-[0.5px]',
                  points < 0 && 'text-destructive',
                  // A nought is the row this side did not score, standing where the other side's
                  // number is. It is drawn back rather than bold, so the column still reads as the
                  // things that HAPPENED with the gaps visible between them.
                  points === 0 && 'text-muted-foreground font-normal',
                )}>
                {points > 0 ? `+${points}` : points}
              </Text>
            </View>
          );
        })
      ) : (
        <Text variant="small" className={cn('text-muted-foreground', ruled && 'border-border border-t py-2')}>
          Nothing yet
        </Text>
      )}
    </View>
  );
}

function Side({ label, value }: { label: string; value: string }) {
  return (
    <View className="bg-card-2 min-w-0 grow basis-[150px] gap-1 rounded-md p-3">
      <Text variant="label">{label}</Text>
      <Text>{value}</Text>
    </View>
  );
}

/**
 * THE GOLD MARK — struck on a thing that is now out of your hands until Sunday.
 *
 * It is GOLD in both sheets and it is two different objects rather than one badge in two colours,
 * which is why it is a plain View and not `Badge variant="outline"`. Bouquet's `.sealed-stamp` is
 * a struck sticker: a filled gold gradient, a darker edge, tilted off the grid. Tuxedo's is the
 * gallery label: hollow, gold-ruled, square to everything around it. A red outline read as an
 * ERROR chip in both — the one colour this app reserves for the primary, on a card announcing a
 * held secret.
 *
 * One component rather than three copies of the markup, because the panel has three commitments
 * and they are the same act: the agenda you sealed, the wager you staked, the pick you locked. A
 * treatment that appears on one of them and not the others says those are different kinds of
 * promise, which they are not — and three copies of a look branch is three places for the gradient
 * and the tracking to drift apart, which is exactly what happened to the gold gradient before it
 * was hoisted into `stamp-band.tsx`.
 *
 * `shrink-0` on purpose: it is a stamp, so it keeps its size, and it is the TITLE beside it that
 * gives ground — see the sealed agenda's row, where the pair is `flex-wrap` and the stamp drops
 * onto its own line rather than squeezing a word to breaking point.
 */
function SealMark({ label, look }: { label: string; look: Look }) {
  return (
    <View
      className={cn(
        'shrink-0 rounded-sm border',
        look === 'bouquet' ? 'bg-gold border-gold-edge px-[11px] py-[5px]' : 'border-gold px-2 py-[5px]',
      )}
      style={
        look === 'bouquet'
          ? [
              { transform: [{ rotate: '-8deg' }] },
              // Web-only, and the `bg-gold` fill underneath it is the native fallback rather than a
              // leftover: a linear-gradient is a CSS value with no RN equivalent, and a flat gold
              // sticker is the honest degradation.
              Platform.OS === 'web' ? ({ backgroundImage: GOLD_GRADIENT } as never) : null,
            ]
          : undefined
      }>
      <Text
        className={cn(
          'font-sans font-bold uppercase',
          look === 'bouquet'
            ? 'text-gold-ink text-[9.5px] leading-[13px] tracking-[1.2px]'
            : 'text-gold-text text-[9px] leading-[12px] tracking-[1.26px]',
        )}>
        {label}
      </Text>
    </View>
  );
}

/**
 * "Gabi’s agenda", or "Their agenda" when the name has not arrived yet.
 *
 * The attributive form, and it is a function because the fallback is the one thing a template
 * literal gets wrong: `${me?.partnerName ?? 'Their'}’s agenda` reads as "Their’s agenda" the whole
 * time /api/me is in flight, which is the panel's first paint. "Theirs" — the standalone form the
 * sealed slot wants — is a different word and is built at that call site.
 */
const possessiveOf = (name: string | undefined) => (name ? `${name}’s` : 'Their');

/** One partner's wager as one line, or the honest absence of one. */
function wagerLine(w: { goal: string; status: string; delta: number } | null | undefined): string {
  if (!w) return '— none staked —';
  return `“${w.goal}” — ${w.status} (${w.delta > 0 ? '+' : ''}${w.delta})`;
}

/**
 * The week, read back — and the one moment on this panel that is allowed to take its time.
 *
 * It used to be a receipt: the card flew off and a plain panel said "Margin: 3" over two tiles,
 * with the partner's wager and the whole co-op challenge — the one thing on the scoreboard neither
 * of them wins alone — computed by the server, frozen onto the season row, and never drawn at all.
 * A drag that deliberate deserves an answer that arrives.
 *
 * So the order is the order somebody reads it in: the numerals above count up first (the panel
 * owns that clock and hands it down as `landed`), and only when they stop does the VERDICT land —
 * the band's own name as the result, with what each of them staked and how the shared challenge
 * ended underneath it. Under reduced motion `landed` is true from the first frame, which is the
 * same card with nothing withheld.
 */
function RevealCard({
  reveal,
  jackpotPoints,
  winnerPick,
  me,
  look,
  landed,
  onPick,
}: {
  reveal: RevealData;
  jackpotPoints: number;
  winnerPick: { type: string; detail: string | null; by: 'A' | 'B' } | null;
  me: Me | null;
  look: Look;
  /** The count-up has finished. Until it has, the card holds everything but its own heading. */
  landed: boolean;
  onPick: (type: string, detail: string | undefined) => void;
}) {
  // The winner's pick is composed here and nowhere else, so its two fields live here too: they
  // are meaningless on a week that has not been revealed, and hoisting them to the panel put two
  // pieces of a form six hundred lines from the form.
  const [pickType, setPickType] = React.useState('date_choice');
  const [pickDetail, setPickDetail] = React.useState('');

  const mine = me?.partner ?? null;
  const theirId = mine ? otherPartner(mine) : null;
  // The board renders before /api/me lands, so an unknown viewer reads as "They" rather than as a
  // raw partner id — the one site whose null-`me` fallback differs from whoLabel's.
  const who = (p: 'A' | 'B') => (me ? whoLabel(p, me, { self: 'You' }) : 'They');
  const theirPossessive = possessiveOf(me?.partnerName);
  const band = BAND_COPY[reveal.band];

  /** Who finished ahead, which is not the same question as who WON — a blowout has no winner. */
  const leader: 'A' | 'B' | null =
    reveal.totals.A === reveal.totals.B ? null : reveal.totals.A > reveal.totals.B ? 'A' : 'B';
  const scoreline = reveal.winner
    ? `${who(reveal.winner)} took the week by ${reveal.margin}.`
    : leader
      ? `${who(leader)} finished ${reveal.margin} ahead.`
      : 'Level on points.';

  const myWager = mine ? reveal.wagers[mine] : null;
  const theirWager = theirId ? reveal.wagers[theirId] : null;
  const coop = reveal.coop;

  return (
    <Card>
      <CardContent>
        {/**
         * THE VERDICT LANDS IN THE HEADER BAND ITSELF.
         *
         * The band name used to be a `CardHead` from the first frame — the answer printed above the
         * question, so the card was over before the numbers had finished arriving. It is the card's
         * own title now and it CHANGES: "Counting the week…" while the numerals above are running,
         * the band's own name the moment they stop. One object, two states, and the loudest row on
         * the card is the one carrying the result. Nothing is withheld under reduced motion, where
         * `landed` is true on the first frame.
         */}
        <CardHead>{landed ? band.title : 'Counting the week…'}</CardHead>

        {!landed ? null : (
          <>
            <Text>{scoreline}</Text>
            <Text variant="small" className="text-muted-foreground">
              {band.text}
            </Text>

            <View className="flex-row flex-wrap gap-3">
              <Side label="Your agenda" value={(mine && reveal.agendas[mine]?.title) || '— none picked —'} />
              <Side
                label={`${theirPossessive} agenda`}
                value={(theirId && reveal.agendas[theirId]?.title) || '— none picked —'}
              />
            </View>

            {reveal.jackpot ? (
              // Reward is GOLD and only gold in both looks, so the old `--warning` reading is gone.
              <Text className="text-gold-text font-semibold">Same agenda — jackpot! +{jackpotPoints} each.</Text>
            ) : null}

            {/* BOTH WAGERS, or neither. One partner's staked goal rendered while the other's was
                silently dropped made the week look one-sided when it was not — and a wager goal is
                a public declaration by design (Lost Cities), so there is nothing here to withhold. */}
            {myWager || theirWager ? (
              <View className="flex-row flex-wrap gap-3">
                <Side label="Your wager" value={wagerLine(myWager)} />
                <Side label={`${theirPossessive} wager`} value={wagerLine(theirWager)} />
              </View>
            ) : null}

            {coop ? (
              <Text
                variant="small"
                className={coop.status === 'succeeded' ? 'text-gold-text font-semibold' : 'text-muted-foreground'}>
                {/* The shared challenge, in the words the co-op card uses for the same three
                    endings — a challenge that read "succeeded" here and "Done together" there
                    would be two records of one night. */}
                Joint challenge · {coop.title} —{' '}
                {coop.status === 'succeeded'
                  ? `done together, ${coop.progress} of ${coop.target} kept.`
                  : coop.status === 'failed'
                    ? `the budget ran out at ${coop.misses} of ${coop.budget} misses.`
                    : `${coop.progress} of ${coop.target} kept, ${coop.misses} of ${coop.budget} misses used — unfinished.`}
              </Text>
            ) : null}

            {reveal.pickAvailable && reveal.winner === mine && !winnerPick ? (
              <View className="w-full max-w-[560px] gap-2">
                <CardTitle>Winner’s pick</CardTitle>
                <View className="flex-row flex-wrap gap-2">
                  {/* Tonal, which is `Chip`'s own rule for a row of choices standing above its own
                      call to action: three red pills would out-shout "Lock the pick", the one
                      control on this card that actually spends the prize. */}
                  {PICK_TYPES.map((p) => (
                    <Chip
                      key={p.id}
                      label={p.label}
                      tone="tonal"
                      selected={pickType === p.id}
                      onPress={() => setPickType(p.id)}
                    />
                  ))}
                </View>
                <Input value={pickDetail} onChangeText={setPickDetail} placeholder="Details…" maxLength={200} />
                {/* The third of the panel's commitments, drawn like the other two: full width,
                    closing its own card. */}
                <Button className="w-full" onPress={() => onPick(pickType, pickDetail.trim() || undefined)}>
                  <Text>Lock the pick</Text>
                </Button>
              </View>
            ) : winnerPick ? (
              <View className="flex-row flex-wrap items-start justify-between gap-2">
                <Text className="min-w-0 flex-1">
                  Pick: <Text className="font-semibold">{pickLabel(winnerPick.type)}</Text>
                  {winnerPick.detail ? ` — ${winnerPick.detail}` : ''}
                </Text>
                <SealMark label="Locked in" look={look} />
              </View>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * PROPOSING THE WEEK'S JOINT CHALLENGE — a template you can then argue with.
 *
 * The API has always taken a title, a detail, a target and a budget, and the client only ever sent
 * `templateIndex`: five fixed challenges, take one or take none, on the one card in the app whose
 * whole point is a thing the two of them agreed to. Picking a template now FILLS the three fields
 * rather than replacing them, so the shipped ones stay the fast path and "walk together" can
 * become "walk after dinner, four nights" without anybody leaving the card.
 *
 * The bounds are the server's own (`title` non-empty, target 1–50, budget 0–20), checked here only
 * so the button can say why it is off. The server is still the one that decides.
 */
const COOP_TARGET_MAX = 50;
const COOP_BUDGET_MAX = 20;

interface CoopTemplate {
  title: string;
  detail: string;
  target: number;
  budget: number;
}

function CoopProposal({
  templates,
  onPropose,
}: {
  templates: CoopTemplate[];
  onPropose: (body: { title: string; detail?: string; target: number; budget: number }) => void;
}) {
  const [pick, setPick] = React.useState(0);
  const [title, setTitle] = React.useState(templates[0]?.title ?? '');
  const [detail, setDetail] = React.useState(templates[0]?.detail ?? '');
  const [target, setTarget] = React.useState(String(templates[0]?.target ?? 3));
  const [budget, setBudget] = React.useState(String(templates[0]?.budget ?? 1));

  /** Choosing a template overwrites the fields, which is what "choosing" one has to mean. */
  const choose = (i: number) => {
    const t = templates[i];
    if (!t) return;
    setPick(i);
    setTitle(t.title);
    setDetail(t.detail);
    setTarget(String(t.target));
    setBudget(String(t.budget));
  };

  const targetN = Number.parseInt(target, 10);
  const budgetN = Number.parseInt(budget, 10);
  const badTarget = !Number.isFinite(targetN) || targetN < 1 || targetN > COOP_TARGET_MAX;
  const badBudget = !Number.isFinite(budgetN) || budgetN < 0 || budgetN > COOP_BUDGET_MAX;
  const reason = !title.trim()
    ? 'Name the challenge first'
    : badTarget
      ? `Nights has to be 1–${COOP_TARGET_MAX}`
      : badBudget
        ? `Misses allowed has to be 0–${COOP_BUDGET_MAX}`
        : undefined;

  return (
    <Card>
      <CardContent>
        <CardHead>Joint challenge</CardHead>
        <CardDescription>One shared goal, one shared failure budget — never purely adversarial.</CardDescription>
        <View className="w-full max-w-[560px] gap-2">
          {/* Tonal for the same reason the winner's pick is: these five now stand above a form and
              its own "Propose it", and a column of red pills would be the loudest thing on a card
              whose point is the sentence at the bottom of it. */}
          {templates.map((t, i) => (
            <Chip
              key={t.title}
              label={`${t.title} (${t.target}×, ${t.budget} misses)`}
              tone="tonal"
              selected={pick === i}
              onPress={() => choose(i)}
              className="self-start"
            />
          ))}

          <Input value={title} onChangeText={setTitle} placeholder="What are you both doing?" maxLength={80} />
          <Input value={detail} onChangeText={setDetail} placeholder="Any detail (optional)" maxLength={200} />
          {/* Two numbers on one row: they are the terms of the same bargain and are read together —
              "four nights, one miss allowed" is one sentence, not two settings. */}
          <View className="flex-row flex-wrap gap-2">
            <View className="min-w-0 grow basis-[140px] gap-1">
              <Text variant="label">Nights</Text>
              <Input value={target} onChangeText={setTarget} keyboardType="number-pad" maxLength={2} />
            </View>
            <View className="min-w-0 grow basis-[140px] gap-1">
              <Text variant="label">Misses allowed</Text>
              <Input value={budget} onChangeText={setBudget} keyboardType="number-pad" maxLength={2} />
            </View>
          </View>

          <Button
            className="w-full"
            disabled={!!reason}
            reason={reason}
            onPress={() =>
              onPropose({
                title: title.trim(),
                detail: detail.trim() || undefined,
                target: targetN,
                budget: budgetN,
              })
            }>
            <Text>Propose it</Text>
          </Button>
        </View>
      </CardContent>
    </Card>
  );
}
